// flow

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

declare type ServerResult = {
	url: string;
	message: string;
	requestId: string;
	status: number;
	duration: number; // duration in ms of call to url from server
};

class PIIPError extends Error {
	serverResult: ServerResult;

	constructor(serverResult: ServerResult) {
		super(serverResult.message);
		this.serverResult = serverResult;
	}
}

export class PIIP {
	hasValidParams: string;
	piipHost: string;
	accessToken: string;
	clientId: string;

	constructor(piipHost: string, accessToken: string, clientId: string) {
		this.hasValidParams = piipHost && accessToken && clientId;
		this.piipHost = piipHost;
		this.accessToken = accessToken;
		this.clientId = clientId;
	}

	async isOptedOutOfAnalytics(): Promise<boolean> {
		let optedOut = false;

		if (this.hasValidParams) {
			try {
				const piip = await this._requestPIIP();
				// returns an object like:
				// {
				//      response: {"tracking_flag": "PII"},
				//      serverResult: {...}
				// }

				if (piip.response.tracking_flag && piip.response.tracking_flag !== 'PII') {
					// if the value is not PII, then the user has opted out or has an enterprise account
					optedOut = true;
				}

				// console.info(`PIIP Request Info: ${JSON.stringify(piip.serverResult)}`);
			} catch (err) {
				console.error(`PIIP Request Failed: ${JSON.stringify(err)}`);
			}
		}

		return optedOut;
	}

	// PIIP is documented here:
	//
	// https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=bjheadlights&title=PIIP+Engineering#PIIPEngineering-EnvironmentDetails
	//
	// Users can change the setting here:
	//
	// https://account.adobe.com/privacy -- prod
	// https://stage.account.adobe.com/privacy -- stage
	//
	// The IMS client Id must have the 'piip_read' scope, and must be part of the IMS client scope when a user logs in
	//
	_requestPIIP(): Promise<any> {
		return new Promise((resolve, reject) => {
			const request = new XMLHttpRequest();

			const url = `${this.piipHost}/piip/fetch_user_tracking_flag?cache_buster=${Date.now()}`;

			request.open('GET', url, true);
			// note, this does not use the normal Authorization header
			request.setRequestHeader('Access-Token', this.accessToken);
			request.setRequestHeader('x-api-key', this.clientId);
			request.responseType = 'json';
			request.timeout = 10 * 1000; // ten seconds

			const startTime = Date.now();

			request.onload = () => {
				const serverResult = {
					url,
					message: request.statusText,
					requestId: `${startTime}`,
					status: request.status,
					duration: Date.now() - startTime,
				};

				if (request.status === 200) {
					if (request.getResponseHeader('Content-Type')) {
						const contentType = request.getResponseHeader('Content-Type');

						if (contentType) {
							contentType.trim();

							if (contentType.startsWith('application/json')) {
								// we have a json response
								const piipResponse = {
									response: request.response,
									serverResult,
								};

								if (piipResponse.response) {
									resolve(piipResponse);
								} else {
									serverResult.status = 204;
									serverResult.message = 'No response returned from PIIP endpoint';
									reject(new PIIPError(serverResult));
								}
							} else {
								serverResult.status = 415;
								serverResult.message = `Unsupported Content-Type from PIIP endpoint ${contentType}, response=${request.response}`;
								reject(new PIIPError(serverResult));
							}
						} else {
							serverResult.status = 415;
							serverResult.message = 'No Content-Type header from PIIP endpoint';
							reject(new PIIPError(serverResult));
						}
					} else {
						serverResult.status = 415;
						serverResult.message = 'No Content-Type header from PIIP endpoint';
						reject(new PIIPError(serverResult));
					}
				} else {
					reject(new PIIPError(serverResult));
				}
			};

			request.onerror = () => {
				const serverResult = {
					url,
					message: 'request failed with error event, onerror',
					requestId: `${startTime}`,
					// return a 500, Internal Server Error if we can't connect to lib process
					status: 500,
					duration: Date.now() - startTime,
				};

				reject(new PIIPError(serverResult));
			};

			request.ontimeout = () => {
				const serverResult = {
					url,
					message: request.statusText || 'PIIP request timeout',
					requestId: `${startTime}`,
					status: 408,
					duration: Date.now() - startTime,
				};

				reject(new PIIPError(serverResult));
			};

			// send it
			request.send();
		});
	}
}
